import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarDays, CalendarRange } from "lucide-react";
import { format, isToday, isSameDay } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { DateRange } from "react-day-picker";

interface AttendanceFiltersProps {
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  onFilterChange: (from: Date | undefined, to: Date | undefined) => void;
  onTodayClick: () => void;
}

export const AttendanceFilters = ({
  dateFrom,
  dateTo,
  onFilterChange,
  onTodayClick,
}: AttendanceFiltersProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempRange, setTempRange] = useState<DateRange | undefined>(undefined);

  // Check if currently viewing today
  const isViewingToday = dateFrom && dateTo && 
    isSameDay(dateFrom, dateTo) && 
    isToday(dateFrom);

  // Handle popover open/close
  const handleOpenChange = (open: boolean) => {
    if (open) {
      // Al abrir: reiniciar la selección temporal (calendario limpio)
      setTempRange(undefined);
    }
    setIsOpen(open);
  };

  // Handle range selection
  const handleRangeSelect = (range: DateRange | undefined) => {
    // Si el usuario selecciona el mismo día que ya tenía como "from" (sin "to"),
    // significa que quiere seleccionar solo ese día
    if (range?.from && !range?.to && tempRange?.from && !tempRange?.to && isSameDay(range.from, tempRange.from)) {
      onFilterChange(range.from, range.from);
      setIsOpen(false);
      return;
    }
    
    setTempRange(range);
    
    // Close popover when range is complete
    if (range?.from && range?.to) {
      onFilterChange(range.from, range.to);
      setIsOpen(false);
    }
  };

  // Format the date range display text
  const getDateRangeText = () => {
    if (!dateFrom && !dateTo) return "Seleccionar fechas";
    if (dateFrom && dateTo && isSameDay(dateFrom, dateTo)) {
      return format(dateFrom, "dd MMM yyyy", { locale: es });
    }
    const fromText = dateFrom ? format(dateFrom, "dd/MM", { locale: es }) : "...";
    const toText = dateTo ? format(dateTo, "dd/MM/yyyy", { locale: es }) : "...";
    return `${fromText}  →  ${toText}`;
  };

  return (
    <div className="flex flex-col gap-4 p-4 bg-card rounded-lg">
      {/* Título */}
      <h2 className="text-base sm:text-lg font-semibold tracking-wide text-center sm:text-left">REGISTRO DE INGRESOS</h2>
      
      {/* Fila de controles */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Selector de rango con un solo calendario */}
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "flex-1 sm:flex-initial justify-start text-left font-normal min-w-[200px]",
                !dateFrom && !dateTo && "text-muted-foreground"
              )}
            >
              <CalendarRange className="mr-2 h-4 w-4" />
              {getDateRangeText()}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-4 bg-card border-border" align="start">
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-muted-foreground">
                Selecciona el rango de fechas
              </span>
              <Calendar
                mode="range"
                selected={tempRange}
                onSelect={handleRangeSelect}
                locale={es}
                numberOfMonths={1}
              />
            </div>
          </PopoverContent>
        </Popover>

        {/* Botón Hoy */}
        <Button
          variant={isViewingToday ? "default" : "outline"}
          onClick={onTodayClick}
          className="sm:w-auto"
        >
          <CalendarDays className="h-4 w-4 mr-2" />
          Hoy
        </Button>
      </div>
    </div>
  );
};
